import DeleteIcon from '@mui/icons-material/Delete';
import {
	Box,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import { RenewalExposureUpdateRequest } from '../../../types/renewals';
import { FastForm } from '../../fastForm';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import { errorToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import {
	getExposureIndexFromContext,
	getExposureObject,
	getPerilName,
} from '../util_sharedEndorsementAndRenewal';
import AddPerilModal from './AddRenewalPerilModal';

interface Props {
	authData: AuthData;
	userHasWriteAccess?: boolean;
	renewalData: RenewalData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
		marginRight: 'auto',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

/**
 * Added peril on an added or existing exposure on an renewal. The url for this peril will be in the
 * format 'added-{index}', i.e 'added-0'.
 */
const AddedPerilDetails = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const params = useParams();
	const classes = useStyles();
	const navigate = useNavigate();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const { userHasWriteAccess, renewalData, authData } = props;

	const {
		renewalUpdateObj,
		productConfig,
		selectedExposure,
		setLoading,
		fields: form,
		saveFastForm,
	} = renewalData;

	if (!selectedExposure || !renewalUpdateObj) {
		return <LargeCenterSpinner />;
	}

	const { perilId, exposureId } = params;

	// Only added exposures have displayId
	const exposureIsAdded = !(selectedExposure as Exposure).displayId;
	const exposureLocator =
		(selectedExposure as Exposure).locator ||
		(selectedExposure as ExposureCreateRequest).exposureLocator;

	// exposure index--either added or existing
	const exposureIndex = getExposureIndexFromContext(
		renewalUpdateObj,
		exposureLocator,
	);

	// selectedExposure differs from renewalExposureObj because selectedExposure can be Exposure and
	// cannot be RenewalExposureUpdateRequest
	const renewalExposureObj = getExposureObject(
		renewalUpdateObj,
		exposureLocator,
	);
	const selectedPerilIndex = parseInt(perilId.split('-')[1], 10);

	const isInEditMode =
		authData.hasPermission('renewal.update') &&
		renewalUpdateObj.state === 'application';

	if (!Number.isInteger(selectedPerilIndex)) {
		return null;
	}

	const selectedPeril = exposureIsAdded
		? (renewalExposureObj as ExposureCreateRequest).perils?.[selectedPerilIndex]
		: (renewalExposureObj as RenewalExposureUpdateRequest).addPerils?.[
				selectedPerilIndex
		  ];

	if (!selectedPeril) {
		return null;
	}

	const exposureName =
		(selectedExposure as Exposure).name ||
		(selectedExposure as ExposureCreateRequest).exposureName;
	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(exposure) => exposure.name === exposureName,
	);
	const perilConfig = exposureConfig.perils.find(
		(p) => p.name === selectedPeril.name,
	);
	const perilConfigs = exposureConfig.perils;

	if (!perilConfig) {
		return null;
	}

	const displayName = getPerilName(
		selectedPeril,
		perilConfig.displayName,
		selectedExposure,
		renewalUpdateObj,
		null,
	);
	const isEditable =
		authData.hasPermission('renewal.update') &&
		renewalUpdateObj.state === 'application';

	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			const perilLocator = selectedPeril.locator;
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeAddedPeril: {
					perilLocator,
					exposureLocator,
					exposureIsAdded,
					exposureIndex,
				},
			});
			navigate(
				`/policy/${params.policyId}/renewals/${params.renewalId}/exposures/${params.exposureId}/perils`,
			);
		} catch (error) {
			errorToast('Error removing peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderRemovePeril = () => {
		return (
			<Tooltip title={'Remove ' + t('peril.singular.upper')}>
				<IconButton
					aria-label='Remove'
					sx={{ position: 'relative', top: '8px' }}
					onClick={() => setIsConfirmModalOpen(true)}
					className={classes.removeIcon}
				>
					<DeleteIcon
						sx={{
							'&:hover': {
								backgroundColor: '#f4433',
							},
						}}
					/>
				</IconButton>
			</Tooltip>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={selectedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.locator}
			/>
		);
	};

	const formName = exposureId.includes('added')
		? `exposure-${exposureIndex}.peril-${selectedPerilIndex}`
		: exposureLocator + '.' + `peril-${selectedPerilIndex}`;

	return (
		<>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{displayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={Link}
						to={`auxData`}
					>
						Aux Data
					</MaterialLink>
				)}
				{isEditable && renderRemovePeril()}
				{isConfirmModalOpen && renderRemovePerilModal()}
			</Box>
			<hr />
			{!hidePerilLimitsEnabled ? <h4>Limits</h4> : null}
			<Grid container spacing={2}>
				<FastForm
					formName={formName}
					form={form}
					configVersion={productConfig.configVersion}
					authData={props.authData}
					productName={productConfig.name}
					disabled={!isInEditMode || userHasWriteAccess}
					save={saveFastForm}
					setLoading={setLoading}
					disableExternalCalls={true}
					effectiveDate={renewalUpdateObj.overrideResourcesAsOfTimestamp}
				/>
			</Grid>
			{isInEditMode && renderActions()}
		</>
	);
};

export default withRenewalContext<Props>(AddedPerilDetails);
